import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import styles from '../Slider.module.css';

const VideoPlayer = forwardRef(({ src, webm, mobileSrc, mobileWebm, poster, isActive, onLoad }, ref) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isLowPowerMode, setIsLowPowerMode] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isHighQuality, setIsHighQuality] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const playAttemptRef = useRef(0);
  const maxPlayAttempts = 3;
  const observerRef = useRef(null);
  const qualityCheckTimeout = useRef(null);
  const visibilityTimeout = useRef(null);
  const [useDesktopFallbackOnMobile, setUseDesktopFallbackOnMobile] = useState(false);

  // Detect device type and iOS
  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera\smini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      
      setIsMobile(isMobileDevice);
      setIsIOS(isIOSDevice);

      if (navigator.getBattery) {
        navigator.getBattery().then(battery => {
          setIsLowPowerMode(battery.charging === false && battery.level < 0.2);
        });
      }
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Check network quality and buffer status
  useEffect(() => {
    if (isMobile) {
      const checkNetworkQuality = async () => {
        try {
          const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
          if (connection) {
            setIsHighQuality(connection.effectiveType === '4g');
          } else {
            const startTime = performance.now();
            await fetch('/test-connection.txt', { cache: 'no-store' });
            const endTime = performance.now();
            setIsHighQuality(endTime - startTime < 200);
          }
        } catch (err) {
          console.error('Network quality check failed:', err);
          setIsHighQuality(false);
        }
      };

      checkNetworkQuality();
      qualityCheckTimeout.current = setInterval(checkNetworkQuality, 30000);

      // Monitor buffering
      const videoElement = videoRef.current;
      if (videoElement) {
        const handleBuffering = () => {
          setIsBuffering(true);
          if (isLowPowerMode || !isHighQuality) {
            videoElement.playbackRate = 0.8;
          }
        };

        const handleBufferEnd = () => {
          setIsBuffering(false);
          videoElement.playbackRate = 1.0;
        };

        videoElement.addEventListener('waiting', handleBuffering);
        videoElement.addEventListener('playing', handleBufferEnd);

        return () => {
          videoElement.removeEventListener('waiting', handleBuffering);
          videoElement.removeEventListener('playing', handleBufferEnd);
        };
      }
    }

    return () => {
      if (qualityCheckTimeout.current) {
        clearInterval(qualityCheckTimeout.current);
      }
    };
  }, [isMobile, isLowPowerMode, isHighQuality]);

  // Intersection Observer for visibility - only on mobile
  useEffect(() => {
    if (isMobile) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            setIsVisible(entry.isIntersecting);
            if (entry.isIntersecting && videoRef.current) {
              // Clear any existing timeout
              if (visibilityTimeout.current) {
                clearTimeout(visibilityTimeout.current);
              }
              // Set a small delay before loading to prevent rapid load/unload
              visibilityTimeout.current = setTimeout(() => {
                videoRef.current.load();
                attemptPlay();
              }, 100);
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: '50px'
        }
      );

      const videoElement = videoRef.current;
      if (videoElement) {
        observerRef.current.observe(videoElement);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (visibilityTimeout.current) {
        clearTimeout(visibilityTimeout.current);
      }
    };
  }, [isMobile]);

  const attemptPlay = useCallback(async () => {
    if (!videoRef.current) return;
    if (isMobile && !isVisible) return;

    try {
      if (isIOS) {
        videoRef.current.setAttribute('playsinline', '');
        videoRef.current.setAttribute('webkit-playsinline', '');
        videoRef.current.setAttribute('x-webkit-airplay', 'deny');
        videoRef.current.style.transform = 'translateZ(0)';
        videoRef.current.style.webkitTransform = 'translateZ(0)';
        videoRef.current.playbackRate = 1.0;
        await new Promise(resolve => setTimeout(resolve, 100));
      } else if (isMobile) {
        videoRef.current.playbackRate = 1.0;
        videoRef.current.preload = 'metadata';
        
        if (isLowPowerMode || !isHighQuality) {
          videoRef.current.playbackRate = 0.8;
          if (!isHighQuality) {
            videoRef.current.currentTime = 0;
            videoRef.current.load();
          }
        }
      }

      // Add a small delay before playing to ensure proper loading
      await new Promise(resolve => setTimeout(resolve, 50));
      await videoRef.current.play();
      playAttemptRef.current = 0;
    } catch (err) {
      console.error('Error playing video:', err);
      setError(err);
      
      // Handle power-saving interruptions
      if (err.name === 'AbortError' && err.message.includes('power')) {
        if (playAttemptRef.current < maxPlayAttempts) {
          playAttemptRef.current++;
          // Exponential backoff for retry attempts
          setTimeout(attemptPlay, Math.min(1000 * Math.pow(2, playAttemptRef.current), 5000));
        }
      } else if (isIOS && playAttemptRef.current < maxPlayAttempts) {
        playAttemptRef.current++;
        setTimeout(attemptPlay, 500 * playAttemptRef.current);
      }
    }
  }, [isIOS, isMobile, isLowPowerMode, isVisible, isHighQuality]);

  useImperativeHandle(ref, () => ({
    play: async () => {
      await attemptPlay();
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    reset: () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    }
  }));

  useEffect(() => {
    if (isActive && (!isMobile || isVisible)) {
      if (videoRef.current) {
        setIsLoading(true);
        setError(null);
        playAttemptRef.current = 0;
        
        if (isIOS) {
          videoRef.current.preload = 'metadata';
          videoRef.current.playsInline = true;
          videoRef.current.setAttribute('playsinline', '');
          videoRef.current.setAttribute('webkit-playsinline', '');
          videoRef.current.setAttribute('x-webkit-airplay', 'deny');
          videoRef.current.style.transform = 'translateZ(0)';
          videoRef.current.style.webkitTransform = 'translateZ(0)';
          videoRef.current.playbackRate = 1.0;
        } else if (isMobile) {
          videoRef.current.preload = 'metadata';
          videoRef.current.playsInline = true;
          
          if (isLowPowerMode || !isHighQuality) {
            videoRef.current.playbackRate = 0.8;
          }
        }
        
        videoRef.current.load();
        
        // Start playing with a small delay to ensure proper loading
        setTimeout(() => {
          attemptPlay();
        }, isMobile ? 100 : 0);
      }
    } else {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      setIsLoading(true);
      setError(null);
    }
  }, [isActive, src, webm, mobileSrc, mobileWebm, isMobile, isIOS, isLowPowerMode, attemptPlay, isVisible, isHighQuality]);

  const handleLoadedData = () => {
    setIsLoading(false);
    setError(null);
    if (onLoad) onLoad();
  };

  const handleEnded = async () => {
    try {
      // Fallback for some mobile browsers where loop can be unreliable
      if (!videoRef.current) return;
      // Only attempt to loop when this slide is active and visible
      if (isActive && (!isMobile || isVisible)) {
        videoRef.current.currentTime = 0;
        // Small delay can help on iOS before replay
        await new Promise(resolve => setTimeout(resolve, 20));
        await videoRef.current.play();
      }
    } catch (err) {
      // Swallow errors silently; attemptPlay will handle retries if needed
    }
  };

  const handleError = (e) => {
    console.error('Video error:', {
      error: e,
      isMobile,
      isIOS,
      srcTried: isMobile && !useDesktopFallbackOnMobile ? (isIOS ? mobileSrc : mobileSrc) : src,
    });
    setError(e);
    setIsLoading(false);

    // If mobile source fails, fall back to desktop source and retry once
    if (isMobile && !useDesktopFallbackOnMobile) {
      setUseDesktopFallbackOnMobile(true);
      setTimeout(() => {
        if (!videoRef.current) return;
        setIsLoading(true);
        setError(null);
        videoRef.current.load();
        attemptPlay();
      }, 50);
    }
  };

  const resolvedMp4Src = (() => {
    if (isMobile) {
      return useDesktopFallbackOnMobile ? src : mobileSrc;
    }
    return src;
  })();

  const videoSrc = isIOS ? resolvedMp4Src : resolvedMp4Src;
  const videoWebm = isIOS ? null : (isMobile && !useDesktopFallbackOnMobile ? mobileWebm : webm);

  return (
    <div className={styles.videoContainer}>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      )}
      {isBuffering && (
        <div className={styles.bufferingOverlay}>
          <div className={styles.bufferingSpinner}></div>
        </div>
      )}
      {error && !isLoading && (
        <div className={styles.error}>
          <p>Video yüklenirken bir hata oluştu.</p>
          <button 
            className={styles.retryButton}
            onClick={() => {
              setError(null);
              setIsLoading(true);
              playAttemptRef.current = 0;
              videoRef.current?.load();
              attemptPlay();
            }}
          >
            Tekrar Dene
          </button>
        </div>
      )}
      <video
        ref={videoRef}
        className={`${styles.video} ${!isLoading ? styles.loaded : ''}`}
        poster={poster}
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="deny"
        crossOrigin="anonymous"
        muted
        loop
        preload={isIOS || isMobile ? 'metadata' : 'auto'}
        onLoadedData={handleLoadedData}
        onEnded={handleEnded}
        onError={handleError}
        onStalled={() => {
          // Try to resume playback on stall
          attemptPlay();
        }}
        style={{
          transform: isMobile ? 'translateZ(0)' : 'none',
          WebkitTransform: isMobile ? 'translateZ(0)' : 'none',
          backfaceVisibility: isMobile ? 'hidden' : 'visible',
          WebkitBackfaceVisibility: isMobile ? 'hidden' : 'visible',
          perspective: isMobile ? '1000' : 'none',
          WebkitPerspective: isMobile ? '1000' : 'none'
        }}
      >
        {videoWebm && <source src={videoWebm} type="video/webm" />}
        {videoSrc && <source src={videoSrc} type="video/mp4" />}
        Tarayıcınız video oynatmayı desteklemiyor.
      </video>
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer; 